/**
 * Nível do consultor (AN-10 / L15 · Q26 — módulo oportunidades, V58).
 *
 * O nível é cadastrado pelo ADMIN, atribuído manualmente e é APENAS INFORMATIVO: não altera
 * comissão, acesso, ranking ou meta. Nenhum nome, cor ou critério de nível existe no código —
 * tudo vem do catálogo. Os tipos ficam isolados aqui para o ajuste de nomes na integração com
 * o backend do DEV-Q (contrato de referência: §8 do desenho AN-10).
 */

/** Nível como o consultor (e o gerente-gestor) o veem: só nome, cor e descrição. */
export interface NivelResumido {
  nome: string
  /** `#RRGGBB`. */
  cor: string
  descricao: string
}

/** `GET /v1/niveis-consultor/meu` — `nivel: null` = sem nível (ou back-office). */
export interface MeuNivelResponse {
  nivel: NivelResumido | null
}

/** `GET /v1/niveis-consultor` (ADMIN) — item do catálogo. */
export interface NivelConsultor {
  id: string
  nome: string
  descricao: string
  cor: string
  ordem: number
  ativo: boolean
  /** Consultores com este nível atribuído (vem pronto do backend). */
  consultores: number
  /** Só com a flag de critérios ligada; com a flag desligada vem `null`. Nunca interpretado. */
  criteriosTexto?: string | null
  /** Lock otimista (`@Version`) — vai de volta no `PUT`. */
  versao: number
  criadoEm?: string | null
  atualizadoEm?: string | null
}

/** `POST /v1/niveis-consultor` e `PUT /v1/niveis-consultor/{id}`. */
export interface NivelConsultorRequest {
  nome: string
  descricao: string
  cor: string
  /** Opcional na criação (padrão do backend = maior + 1). */
  ordem?: number | null
  /** Só enviado com a flag ligada (flag desligada → 422 NIVEL_CRITERIOS_DESABILITADOS). */
  criteriosTexto?: string | null
  /** Obrigatório no `PUT` (409 NIVEL_ALTERADO_POR_OUTRO). */
  versao?: number
}

/** `PUT /v1/niveis-consultor/ordem` — lista COMPLETA de ids na nova ordem. */
export interface OrdemNiveisRequest {
  ids: string[]
}

/** `PATCH /v1/niveis-consultor/{id}/status`. */
export interface StatusNivelRequest {
  ativo: boolean
}

/** `GET /v1/niveis-consultor/configuracao` (ADMIN). */
export interface ConfiguracaoNivel {
  criteriosAutomacaoHabilitado: boolean
}

/** Nível resolvido numa linha de atribuição. */
export interface NivelDaLinha extends NivelResumido {
  id: string
  ativo: boolean
}

/** Linha de `GET /v1/niveis-consultor/atribuicoes` (ADMIN e GERENTE+GESTOR_EQUIPE, recortada). */
export interface AtribuicaoNivelLinha {
  consultorId: string
  nome?: string | null
  canal?: string | null
  nivel: NivelDaLinha | null
  /** Só para o ADMIN (o gerente recebe `null`/ausente). */
  atribuidoEm?: string | null
}

/**
 * Página de `GET /v1/niveis-consultor/atribuicoes` — o `PageResponse` do originei-common
 * (`pageNumber`/`pageSize`, base 0), não o `Page` do Spring que `@/types/comum` descreve.
 */
export interface PaginaAtribuicoes {
  content: AtribuicaoNivelLinha[]
  pageNumber: number
  pageSize: number
  totalElements: number
  totalPages: number
  first: boolean
  last: boolean
  empty: boolean
}

export interface FiltroAtribuicoes {
  busca?: string
  nivelId?: string
  semNivel?: boolean
  emNivelInativo?: boolean
  page?: number
  size?: number
}

/** `PUT /v1/niveis-consultor/atribuicoes/{consultorId}` (ADMIN). `nivelId: null` = remover. */
export interface AtribuicaoNivelRequest {
  nivelId: string | null
  justificativa: string
  /** Nível que a tela mostrava como atual (409 NIVEL_ATRIBUICAO_CONCORRENTE se divergir). */
  nivelAtualEsperadoId: string | null
}

/** `GET /v1/niveis-consultor/atribuicoes/{consultorId}/historico` (ADMIN) — append-only. */
export interface HistoricoNivel {
  id: string
  consultorId: string
  nivelAnteriorId?: string | null
  /** Nome congelado na época (renomear depois não muda o histórico). */
  nivelAnteriorNome?: string | null
  nivelNovoId?: string | null
  nivelNovoNome?: string | null
  justificativa: string
  autorId: string
  autorNome?: string | null
  autorPapel: string
  criadoEm: string
}

// ----- Limites (espelho dos CHECKs da V58) -----

export const NIVEL_NOME_MAX = 60
export const NIVEL_DESCRICAO_MAX = 1000
export const JUSTIFICATIVA_NIVEL_MIN = 10
export const JUSTIFICATIVA_NIVEL_MAX = 500
export const COR_NIVEL_REGEX = /^#[0-9A-Fa-f]{6}$/

/** Faixa fixa (Q26, R5): o nível não promete nada. */
export const AVISO_NIVEL_INFORMATIVO =
  'O nível é informativo: não altera comissão, metas, ranking ou acesso.'

// ----- Trilha do catálogo (entidade NIVEL em alteracoes_gestao_comercial) -----

export const ALTERACAO_NIVEL_ROTULO: Record<string, string> = {
  NIVEL_CRIADO: 'Nível criado',
  NIVEL_EDITADO: 'Nível editado',
  NIVEL_ORDEM: 'Ordem alterada',
  NIVEL_STATUS: 'Status alterado',
}

/** Tipo desconhecido (backend mais novo) aparece cru, nunca some. */
export const rotuloAlteracaoNivel = (tipo: string): string => ALTERACAO_NIVEL_ROTULO[tipo] ?? tipo

// ----- Erros -----

/** Texto de apoio quando o backend não manda `message`. */
export const ERRO_NIVEL_ROTULO: Record<string, string> = {
  NIVEL_NOME_DUPLICADO: 'Já existe um nível com esse nome',
  NIVEL_COR_INVALIDA: 'Cor inválida: use o formato #RRGGBB',
  NIVEL_ORDEM_EM_USO: 'Essa ordem já está em uso por outro nível',
  NIVEL_ALTERADO_POR_OUTRO: 'O nível foi alterado por outra pessoa. Recarregue e tente de novo',
  NIVEL_ORDEM_INCOMPLETA: 'A lista de ordem está desatualizada (faltam ou sobram níveis). Recarregue e tente de novo',
  NIVEL_ORDEM_INVALIDA: 'Ordem inválida',
  NIVEL_VERSAO_OBRIGATORIA: 'Versão do nível ausente. Recarregue a página e tente de novo',
  NIVEL_NOME_INVALIDO: `O nome é obrigatório e deve ter até ${NIVEL_NOME_MAX} caracteres`,
  NIVEL_DESCRICAO_INVALIDA: `A descrição é obrigatória e deve ter até ${NIVEL_DESCRICAO_MAX} caracteres`,
  NIVEL_CRITERIOS_DESABILITADOS: 'Critérios de nível estão desativados',
  NIVEL_JUSTIFICATIVA_OBRIGATORIA: `A justificativa deve ter de ${JUSTIFICATIVA_NIVEL_MIN} a ${JUSTIFICATIVA_NIVEL_MAX} caracteres`,
  CONSULTOR_INVALIDO: 'Consultor inválido ou inativo',
  NIVEL_INATIVO: 'Esse nível está inativo e não pode ser atribuído',
  NIVEL_NAO_ENCONTRADO: 'Nível não encontrado',
  NIVEL_ATRIBUICAO_CONCORRENTE: 'O nível deste consultor foi alterado por outra pessoa. Recarregue e tente de novo',
  NIVEL_SEM_MUDANCA: 'O consultor já está nesse nível',
  GESTAO_SEM_PERMISSAO: 'Você não tem permissão para esta ação',
  FORA_DA_EQUIPE: 'Consultor fora da sua equipe',
}

/** Mensagem do backend; sem ela, o rótulo do código; sem ambos, o padrão da tela. */
export const mensagemErroNivel = (e: unknown, padrao: string): string => {
  const env = e as { message?: string; data?: { errorCode?: string; message?: string } } | null
  const msg = env?.message ?? env?.data?.message
  if (msg) return msg
  const codigo = env?.data?.errorCode
  return (codigo && ERRO_NIVEL_ROTULO[codigo]) || padrao
}
